import { isLocalEnvironment } from 'utils/environment';

export type PipelineVersion = 'v1' | 'v2';

export type FallbackReason = 'no_variant_id' | 'variant_not_in_linked_entries' | 'adapter_failure' | 'unexpected_error';

export type Vendor = 'statsig' | 'ninetailed';

export interface PipelineEvent {
  event: string;
  /**
   * Omit for shared/infrastructure events (e.g. Contentful page fetch, proxy
   * Ninetailed call) that run identically under V1 and V2. Set only on events
   * emitted from code paths that are pipeline-specific.
   */
  pipeline?: PipelineVersion;
  durationMs?: number;
  experimentId?: string;
  moduleId?: string;
  /** Page slug — included on per-page events so cacheWriteSkipped, cachePoisoned, etc. can be grouped to identify Contentful entries that need cleanup. */
  slug?: string;
  variantIndex?: number;
  vendor?: Vendor;
  fallbackReason?: FallbackReason;
  selectionCount?: number;
  pageRelevantSelectionCount?: number;
  inIncludesMap?: boolean;
  /** Number of modules considered for Ninetailed data fetch */
  moduleCount?: number;
  /** Modules whose mappedExperiences came back non-empty */
  populatedModuleCount?: number;
  /** Modules whose Contentful fetch failed (returned with an error field) */
  errorModuleCount?: number;
  /** Sum of mappedExperiences.length across all modules */
  totalExperienceCount?: number;
  error?: unknown;
}

const LOG_SOURCE = 'experimentation-pipeline';

/**
 * Emits a structured log line for the experimentation pipeline.
 *
 * next-logger patches `console.warn` to pino, so passing an object produces
 * Datadog-queryable JSON fields. Everything is emitted at warn level to stay
 * within the repo's `no-console` convention (only warn/error allowed); consumers
 * filter on `@event` and `@fallbackReason` in Datadog to separate timing events
 * from actual problems.
 */
export function logPipelineEvent(payload: PipelineEvent): void {
  const isProblem = payload.fallbackReason !== undefined || payload.error !== undefined;
  // Silence the per-request timing flood in `yarn dev`; always surface
  // fallbacks and errors so local misconfig is still obvious.
  if (isLocalEnvironment() && !isProblem) return;
  console.warn({ source: LOG_SOURCE, ...payload });
}

/** Monotonic clock with a Date.now() fallback for environments without performance. */
export function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}
